import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Button } from 'tea-component';
import { getLocale, history, useIntl,useLocation } from 'umi';
import EventTypeTag from '../EventTypeTag';
import { customTagColor, tagTypeArr } from '../_config/constant';
import { getSubtitleDate } from '../_config/utils';
import './index.scss';

/** KPI：`eventTypeBarColorMap` 解析后的 Tag 样式（含按类型的 `xAxisBubble` 覆盖浮窗类型标签） */
interface IEventDetailsPopTagStyleEntry {
  background: string;
  fontColor?: string;
}

export type TEventDetailsPopTagStyleMap = Record<string, IEventDetailsPopTagStyleEntry>;

interface IEventTypeNamePair {
  name_en?: string;
  name_cn?: string;
}

function pickTagStyleEntry(
  hit: IEventDetailsPopTagStyleEntry | undefined,
): IEventDetailsPopTagStyleEntry | null {
  if (
    !hit ||
    typeof hit.background !== 'string' ||
    hit.background.trim() === ''
  ) {
    return null;
  }
  const entry: IEventDetailsPopTagStyleEntry = {
    background: hit.background.trim(),
  };
  if (typeof hit.fontColor === 'string' && hit.fontColor.trim() !== '') {
    entry.fontColor = hit.fontColor.trim();
  }
  return entry;
}

function resolveTagStyleFromMap(
  map: TEventDetailsPopTagStyleMap | undefined,
  eventType: IEventTypeNamePair,
): IEventDetailsPopTagStyleEntry | null {
  if (!map || !eventType) {
    return null;
  }
  const en = String(eventType.name_en ?? '').trim();
  const cn = String(eventType.name_cn ?? '').trim();
  const keys = [
    en,
    cn,
    en.toLowerCase(),
    cn.toLowerCase(),
  ].filter(k => k !== '');
  for (const k of keys) {
    const found = pickTagStyleEntry(map[k]);
    if (found) {
      return found;
    }
  }
  const enLo = en.toLowerCase();
  const cnLo = cn.toLowerCase();
  for (const mapKey of Object.keys(map)) {
    const mkLo = mapKey.toLowerCase();
    if ((en && mkLo === enLo) || (cn && mkLo === cnLo)) {
      const found = pickTagStyleEntry(map[mapKey]);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

export default function Index({
  currentEvents,
  timeType,
  isOverview = false,
  pathname,
  popRef,
  className = '',
  style,
  tagStyleMap,
  /** 概览非全屏：每种事件类型最多展示条数；≤0 不截断；默认 3 */
  eventTypeMaxCount = 3,
}: any) {
  const { formatMessage } = useIntl();
  const location = useLocation();
  const local = getLocale();
  const isEnLocal = local === "en-US";
  const getEventsContent = () => {
    // 浮窗最多展示6种事件类型
    const data = currentEvents?.event_list?.slice(0, 6) || [];
    const customArr:string [] = [];
    return data.map((eventData: { event_type: any; event_record?: never[] | undefined; events: any; }, index: any) => {
      const { event_type, event_record = [], events } = eventData;
      const name = isEnLocal ? event_type.name_en : (event_type.name_cn || event_type.name_en);
      const eventLen = isOverview ? events : event_record.length;
      // 每种事件类型最多展示3行事件
      // const content = event_record?.slice(0, 3)?.map((record: { event_type_id: number; countries: { country_cn: any; country_en: any; }[]; event_title_en: {} | null | undefined; }) => {
      //   //事件类型为Holiday时, 要拼接国家名称
      //   if (record?.event_type_id === 4 && record?.countries?.length > 0) {
      //     const countryStr = record.countries.map(({ country_cn, country_en }) => isEnLocal ? country_en : (country_cn || country_en)).join(',')
      //     return (<div key={index}>{record?.event_title_en}-{countryStr}</div>)
      //   } else {
      //     return (<div key={index}>{record?.event_title_en}</div>)
      //   }
      // })
      const maxRows =
        typeof eventTypeMaxCount === 'number' && eventTypeMaxCount > 0
          ? eventTypeMaxCount
          : event_record?.length ?? 0;
      const content = event_record?.slice(0, maxRows)?.map((record, index) => {
        const { event_title_en = '', event_title_cn = '' } = record || {};
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const eventTitle = isEnLocal ? event_title_en : (event_title_cn || event_title_en);

        return (
          <div key={index}>
            {event_title_en}
          </div>
        );
      });


      const type = event_type.name_en?.toLowerCase();
      const isCustom = !tagTypeArr?.includes(type);

      let customColor = resolveTagStyleFromMap(tagStyleMap, event_type);
      if (!customColor && isCustom) {
        if (customArr?.includes(type)) {
          const colorIndex = customArr.findIndex((item) => item === type);
          customColor =
            customTagColor?.[colorIndex] ||
            customTagColor?.[customTagColor?.length - 1];
        } else {
          customArr?.push(type);
          customColor =
            customTagColor?.[customArr?.length - 1] ||
            customTagColor?.[customTagColor?.length - 1];
        }
      }
      return (<div className={`event-type-details ${eventLen === 1 ? 'event-type-details-single' : ''}`} key={event_type.id}>
        <EventTypeTag count={eventLen} customColor={customColor} eventType={event_type?.name_en} colorIndex={index} style={{ width: "88px" }} title={name}></EventTypeTag>
        <div className={'popover-event-desc'}>
          {content}
        </div>
      </div>);
    }) || "";
  };

  return (<div
    className={`popover-event-wrap ${className}`}
    style={style}
    ref={popRef}
    onMouseEnter={() => {
      if (!popRef.current) return;
      popRef.current.style.visibility = 'visible';
      popRef.current.style.opacity = '1';
    }}
    onMouseLeave={() => {
      if (!popRef.current) return;
      popRef.current.style.visibility = 'hidden';
      popRef.current.style.opacity = '0';
    }}
  >
    <div
      className={'popover-event events-popover'}
    >
      <div className={"title-flex"} onClick={() => {
        // 如果从realtime跳转到事件详情，则timeType固定为Day类型
        history.push({
          // @ts-ignore
          pathname, query: {
            timeType: isOverview ? timeType : 'Day',
            date: currentEvents.date,
            dashboardType: isOverview ? 'overview' : 'realtime',
            fromPage: location?.pathname
          }
        });
      }}
      style={{ cursor: "pointer" }}
      >
        <div className="title-flex-text">
          <span className="events-text">{formatMessage({ id: "event_info_title" })} </span>
          <span className="events-year">{`(${currentEvents.events} ${formatMessage({ id: "event_info_events" })})`}</span>

          <span className={"events-time"}>
            {getSubtitleDate(timeType, currentEvents.date, currentEvents.time)}
          </span>
        </div>
        <Button className="title-flex-btn" type="link">{formatMessage({ id: 'event_more' })}</Button>
        {/* {pathname && <SvgIcon type="title-arrow"></SvgIcon>}*/}
      </div>
      <div className={"popover-event-body"}>
        {getEventsContent()}
      </div>
    </div>

    <div
      className={"popover-event-wrap-bottom"}
      onMouseOver={(event) => {
        event.stopPropagation();
        popRef.current.style.visibility = "hidden";
        popRef.current.style.opacity = 0;
      }}
    />
  </div>
  );
}

export function useEventDetailsPop(eventData: any[], popRef: React.MutableRefObject<undefined>, seriesId = "eventDetails") {
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentIndexRef = useRef(0);
  const currentEvents = useMemo(() => eventData?.[currentIndex] || { events: 0 }, [eventData, currentIndex]);

  const slotConfigFunc = useCallback(
    (opt, chart) => {
      // 鼠标hover到圆圈的时候才展示浮窗
      chart?.on("mouseover", (params: { seriesId: string; dataIndex: number; event: { offsetX: any; event: any; }; }) => {
        if (params.seriesId !== seriesId || popRef.current.style.opacity == 1) return;
        setCurrentIndex(params?.dataIndex);
        currentIndexRef.current = params?.dataIndex;

        const { offsetX, event } = params.event;
        // 迁移老版本的代码后发现clientWidth一直为0，这里加上width做兜底
        const chartWidth = event.target.clientWidth || event.target.width; // 图表宽度
        const halfPopWidth = 380 / 2; // 事件浮窗的一半宽度
        // 设置事件浮窗 坐标位置的三种情况
        // 当鼠标 x 坐标超过 chartWidth - halfPopWidth
        // 当鼠标 x 坐标小于 halfPopWidth
        // 其他坐标
        const left = offsetX > (chartWidth - halfPopWidth) ? chartWidth - 380 : (offsetX < halfPopWidth ? 0 : offsetX - halfPopWidth);

        popRef.current.style.visibility = "visible";
        popRef.current.style.opacity = 1;
        popRef.current.style.transform = `translate3d(${left}px, 0px, 0px)`;

      });
      // 当两个圆圈距离非常近甚至重叠时, 那么就是鼠标移动事件, 更新当前的事件详情.
      chart?.on('mousemove', (params) => {
        if ((params.seriesId !== seriesId) || (params?.dataIndex === currentIndexRef.current)) return;
        setCurrentIndex(params?.dataIndex || 0);
        currentIndexRef.current = params?.dataIndex || 0;
      });

      chart?.on("mouseout", () => {
        popRef.current.style.visibility = "hidden";
        popRef.current.style.opacity = 0;
      });

      // 鼠标移出图表事件
      chart?.on("globalout", () => {
        // Overview页面中, 鼠标在两个图表之间快速滑动时, 有时不会触发mouseout事件, 导致事件详情浮窗无法消失
        // 添加鼠标移出图表事件, 设置浮窗隐藏.
        popRef.current.style.visibility = "hidden";
        popRef.current.style.opacity = 0;
      });
      opt?.tooltip?.extraCssText  &&(opt.tooltip.extraCssText = "z-index: 101");

      const seriesData = eventData?.map(({ events }, index) => {
        let width = 20;
        let text = events;
        if (events >= 99) {
          width = 32;
          text = "99+";
        } else if (events > 10 && events < 99) {
          width = 20;
        } else {
          width = 14;
        }
        return {
          name: index,
          value: 0,
          label: {
            show: events > 0,
            width,
            formatter: () => text,
          }
        };
      }) || [];
      opt.series.push({
        id: seriesId,
        type: "line",
        showSymbol: true,
        showAllSymbol: false,
        data: seriesData,
        // lineStyle: {
        //   color: "#f2f3f5",
        // },
        tooltip: {
          show: false
        },
        symbolSize: 0,
        itemStyle: {
          color: "rgba(0, 0, 0, 0)",
        }, // 圆形样式

        label: { // 使用label实现 圆形, 椭圆形的效果. 同时设置itemStyle的透明度为0, 对拐点圆圈实现隐藏的效果.
          show: true,
          backgroundColor: "#fff",
          borderWidth: 1,
          borderColor: "#efeff2",
          fontSize: 8,
          fontWeight: 500,
          color: "#9B9FB0",
          position: "inside",
          verticalAlign: "middle",
          // offset: [0, 5],
          // padding:[2,0],
          width: 20,
          height: 14,
          borderRadius: 5,
        },
        emphasis: { // 高亮的图形和标签样式
          disabled: true,
          scale: false, // 关闭高亮放大效果
          label: {
            color: "#fff",
            backgroundColor: "#0076FF",
            borderColor: "#0076FF",
          }
        },
        z: 10
      });
      return opt;

    }, [JSON.stringify(eventData), popRef, seriesId]);

  return {
    slotConfigFunc,
    currentEvents,
  };
}
